import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Button, ButtonDropdown, ButtonDropdownProps,
    Cards, CardsProps, Header, Icon, NonCancelableCustomEvent, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { createRef, useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';

import ArrowDown from '../../../common/dashboard/images/arrow-down.svg';
import ArrowDownDisabled from '../../../common/dashboard/images/arrow-down-disabled.svg';
import ArrowLeft from '../../../common/dashboard/images/arrow-left.svg';
import ArrowLeftDisabled from '../../../common/dashboard/images/arrow-left-disabled.svg';
import ArrowRight from '../../../common/dashboard/images/arrow-right.svg';
import ArrowRightDisabled from '../../../common/dashboard/images/arrow-right-disabled.svg';
import ArrowUp from '../../../common/dashboard/images/arrow-up.svg';
import ArrowUpDisabled from '../../../common/dashboard/images/arrow-up-disabled.svg';
import DashboardsLocalStorage, { DashboardChart } from '../../../common/DashboardLocalStorage';
import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { deepCopy } from '../../../common/util';
import { CookieConsentContext } from '../../../contexts/CookieConsentContext';
import ConfirmationModal from '../../common/ConfirmationModal';
import { ColumnOrderType } from '../../portfolio-data-asset/constants';
import { SchemaPropertyMap } from '../../portfolio-data-asset/types';
import { generateColumnDefs } from '../../portfolio-data-asset/utils';
import { Chart } from './Chart';
import { CARDS_PER_ROW, getChartActions } from './constants';
import { defaultCharts } from './defaultCharts';
import { CategoryOption, ChartData, ChartDataTypes } from './interfaces';
import NewChartModal from './NewChartModal';

type Props = {
    activeDashboard: string;
    data: ChartData;
    dataLoading: boolean;
    schemaPropertyMap: SchemaPropertyMap;
} & FlashContextInterface;

const DashboardTab = ({ activeDashboard, data, dataLoading, schemaPropertyMap, flash }: Props): JSX.Element => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/',
    });
    const portfolioId = match?.params.portfolioId || '';
    const [ initialCharts, setInitialCharts ] = useState<DashboardChart[]>([]);
    const [ charts, setCharts ] = useState<DashboardChart[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ isNewChart, setIsNewChart ] = useState(true);
    const [ isNewChartModalVisible, setIsNewChartModalVisible ] = useState(false);
    const [ chartToEditCloneOrRemove, setChartToEditCloneOrRemove ] = useState<DashboardChart>();
    const [ categoryOptions, setCategoryOptions ] = useState<CategoryOption[]>([]);
    const [ removeConfirmationVisible, setRemoveConfirmationVisible ] = useState(false);
    const [ removeAllChartsConfirmationVisible, setRemoveAllChartsConfirmationVisible ] = useState(false);
    const [ restoreDefaultsConfirmationVisible, setRestoreDefaultsConfirmationVisible ] = useState(false);
    const [ inArrangeMode, setInArrangeMode ] = useState(false);

    const { getLocalStorage, setLocalStorage } = useContext(CookieConsentContext);

    const cardRef = createRef<HTMLDivElement>();

    const loadData = () => {
        const result = DashboardsLocalStorage.loadDashboard(portfolioId, activeDashboard, getLocalStorage);
        let dashboardCharts = [];
        if (!result && activeDashboard === 'default') {
            saveDashboard(defaultCharts, 'default');
            dashboardCharts = defaultCharts;
        } else {
            dashboardCharts = result.charts;
        }
        setInitialCharts(dashboardCharts);
        setCharts(dashboardCharts);
    };

    const loadSchemaPropertyMap = () => {
        const columnDefinitions = generateColumnDefs(data.servers, schemaPropertyMap, 
            ColumnOrderType.SERVER, ['portfolioId', 'migrationPlan', 'migrationPlanOverride']);
        if (columnDefinitions.length > 0) {
            const categoryOptions = columnDefinitions.map(column => {
                const option: CategoryOption = {
                    value: column.id,
                    label: column.text ?? '',
                    source: 'server'
                };
                return option;
            });

            categoryOptions.push({value: 'instanceTypes:instanceType', label: constants.AWS_INSTANCE_TYPE});
            categoryOptions.push({value: 'instanceTypes:totalCost', label: constants.TOTAL_COST_PER_INSTANCE});
            categoryOptions.push({value: 'instanceTypes:numInstances', label: constants.NUMBER_OF_INSTANCES});
            setCategoryOptions(categoryOptions);
        }
    };

    useEffect(() => {
        if (!data || dataLoading) {
            return;
        }
        setLoading(true);
        try {
            loadData();
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, '');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ activeDashboard, portfolioId, data ]);

    useEffect(() => {
        if (schemaPropertyMap) {
            loadSchemaPropertyMap();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ schemaPropertyMap ]);

    const saveDashboard = (charts: DashboardChart[], dashboardName: string) => {
        DashboardsLocalStorage.saveDashboard(portfolioId, dashboardName, { charts }, getLocalStorage, setLocalStorage);
    };

    const save = (updatedCharts: DashboardChart[]) => {
        setCharts(updatedCharts);
        setInitialCharts(updatedCharts);
        saveDashboard(updatedCharts, activeDashboard || constants.DEFAULT_LOWERCASE);
    };

    const restoreDefaultCharts = () => {
        setRestoreDefaultsConfirmationVisible(false);
        save(deepCopy(defaultCharts));
    };

    const addNewChart = () => {
        setIsNewChart(true);
        setChartToEditCloneOrRemove(undefined);
        setIsNewChartModalVisible(true);
    };

    const saveChart = (chart: DashboardChart) => {
        const updatedCharts: DashboardChart[] = deepCopy(charts);
        if (isNewChart) {
            updatedCharts.push(chart);
        } else if (chartToEditCloneOrRemove) {
            const chartIndex = charts.indexOf(chartToEditCloneOrRemove);
            if (chartIndex > -1) {
                updatedCharts.splice(chartIndex, 1, chart);
            }
        } else {
            return;
        }
        save(updatedCharts);
    };

    const leaveArrangeMode = (saveChanges: boolean) => {
        setInArrangeMode(false);
        if (saveChanges) {
            save(charts);
        } else {
            setCharts(initialCharts);
        }
    };

    const headerActions = inArrangeMode ? (
        <SpaceBetween size='xs' direction='horizontal'>
            <Button onClick={() => leaveArrangeMode(false)} >{constants.CANCEL_BUTTON_TEXT}</Button>
            <Button onClick={() => leaveArrangeMode(true)} variant='primary'>{constants.SAVE_BUTTON_TEXT}</Button>
        </SpaceBetween>
    ) : (
        <SpaceBetween size='xs' direction='horizontal'>
            { loading ? (
                <Spinner size='big'/>
            ) : (
                <Icon name='status-positive' variant='success' size='big'/>
            )}
            <Button onClick={() => setInArrangeMode(!inArrangeMode)} disabled={!(charts.length > 1)}>{constants.ARRANGE}</Button>
            <Button onClick={() => setRemoveAllChartsConfirmationVisible(true)} disabled={charts.length === 0}>{constants.REMOVE_ALL}</Button> 
            <Button onClick={() => setRestoreDefaultsConfirmationVisible(true)}>{constants.RESTORE_DEFAULTS}</Button>
            <Button variant='primary' onClick={addNewChart}>{constants.ADD_BUTTON_TEXT}</Button>
        </SpaceBetween>
    );

    const getDataSource = (chart: DashboardChart): ChartDataTypes[] => {
        return data[chart.sourceName as keyof ChartData] ?? data.servers;
    };

    const onChartActionClick = (item: DashboardChart) => (event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>) => {        
        switch(event.detail.id) {
            case 'showLabels':
                setLabelState(true, item);
                break;
            case 'hideLabels':
                setLabelState(false, item);
                break;
            case 'clone':
                cloneChart(item);
                break;
            case 'edit':
                editChart(item);
                break;
            case 'remove':
                confirmRemoveChart(item);
                break;
        }
    };

    const setLabelState = (showLabel: boolean, chart: DashboardChart) => {
        const chartIndex = charts.findIndex(initialChart => initialChart === chart);
        if (chartIndex > -1) {
            const updatedCharts = deepCopy(charts);
            updatedCharts.splice(chartIndex, 1, {...chart, withLabels: showLabel});
            save(updatedCharts);
        }
    };

    const cloneChart = (chart: DashboardChart) => {
        setIsNewChart(true);
        setChartToEditCloneOrRemove(chart);
        setIsNewChartModalVisible(true);
    };

    const editChart = (chart: DashboardChart) => {
        setIsNewChart(false);
        setChartToEditCloneOrRemove(chart);
        setIsNewChartModalVisible(true);
    };

    const confirmRemoveChart = (chart: DashboardChart) => {
        setChartToEditCloneOrRemove(chart);
        setRemoveConfirmationVisible(true);
    };

    const onConfirmRemoveChart = () => {
        setRemoveConfirmationVisible(false);
        const chartIndex = charts.findIndex(initialChart => initialChart === chartToEditCloneOrRemove);
        if (chartIndex > -1) {
            const updatedCharts = deepCopy(charts);
            updatedCharts.splice(chartIndex, 1);
            setCharts(updatedCharts);
        } else {
            flash.alert(FlashType.error, constants.UNABLE_TO_REMOVE_CHART, '');
        }
    };

    const onConfirmRemoveAllCharts = () => {
        setRemoveAllChartsConfirmationVisible(false);
        save([]);
    };

    const canMove = (chart: DashboardChart, cardsPerRow: number): boolean => {
        const chartIndex = charts.findIndex(initialChart => initialChart === chart);
        return ((chartIndex + cardsPerRow) >= 0 && (chartIndex + cardsPerRow) < charts.length);
    };

    const allowMoveLeft = (chart: DashboardChart): boolean => {
        const chartIndex = charts.findIndex(initialChart => initialChart === chart);
        return canMove(chart, -1) && (chartIndex % CARDS_PER_ROW !== 0);
    };

    const allowMoveRight = (chart: DashboardChart): boolean => {
        const chartIndex = charts.findIndex(initialChart => initialChart === chart);
        return canMove(chart, 1) && (chartIndex % CARDS_PER_ROW !== CARDS_PER_ROW - 1);
    };

    const allowMoveUp = (chart: DashboardChart): boolean => {
        return canMove(chart, -CARDS_PER_ROW);
    };

    const allowMoveDown = (chart: DashboardChart): boolean => {
        return canMove(chart, CARDS_PER_ROW);
    };

    const move = (chart: DashboardChart, offset: number) => {
        const chartIndex = charts.findIndex(initialChart => initialChart === chart);
        if (chartIndex + offset >= 0 && chartIndex + offset < charts.length) {
            const updatedCharts = deepCopy(charts);
            updatedCharts.splice(chartIndex, 1);
            updatedCharts.splice(chartIndex + offset, 0, chart);
            setCharts(updatedCharts);
        }
    };

    const cardDefinition: CardsProps.CardDefinition<DashboardChart> = {
        header: (item: DashboardChart) => (
            <Box>
                <Box variant='h3' display='inline'>{item.title}</Box>
                <Box float='right' display='inline'>
                    {inArrangeMode ? (
                        <SpaceBetween size='xs' direction='horizontal'>
                            <Button iconUrl={allowMoveUp(item) ? ArrowUp : ArrowUpDisabled} onClick={() => move(item, -CARDS_PER_ROW)}></Button>
                            <Button iconUrl={allowMoveDown(item) ? ArrowDown : ArrowDownDisabled} onClick={() => move(item, CARDS_PER_ROW)}></Button>
                            <Button iconUrl={allowMoveLeft(item) ? ArrowLeft : ArrowLeftDisabled} onClick={() => move(item, -1)}></Button>
                            <Button iconUrl={allowMoveRight(item) ? ArrowRight : ArrowRightDisabled} onClick={() => move(item, 1)}/>
                        </SpaceBetween>
                    ) : (
                        <ButtonDropdown 
                            items={getChartActions(item.withLabels ?? false)}
                            onItemClick={onChartActionClick(item)}
                        >{constants.ACTION_BUTTON_TEXT}</ButtonDropdown>
                    ) }
                </Box>
            </Box>
        ),
        sections: [
            {
                content: (item: DashboardChart) => (
                    <div ref={cardRef}>
                        <SpaceBetween size='s'>
                            <Box variant='div'>{item.description}</Box>
                            <Chart<ChartDataTypes>
                                dashboardChart={item}
                                dataSource={getDataSource(item)}
                                dataLoading={dataLoading}
                                containerRef={cardRef}
                            />
                        </SpaceBetween>
                    </div>
                )
            }
        ]
    };

    const cards = (
        <Cards
            trackBy={'id'}
            items={charts}
            cardDefinition={cardDefinition}
            header={
                <Header
                    variant='h2'
                    counter={`(${charts.length})`}
                    actions={headerActions}
                >
                    {activeDashboard} {constants.CHARTS_LOWERCASE}
                </Header>
            }
            cardsPerRow={[
                { cards: CARDS_PER_ROW },
            ]}
        />
    );

    if (loading || dataLoading) {
        return (
            <Spinner size='big'/>
        );
    } else {
        return (
            <SpaceBetween size='s'>
                {cards}
                { !charts.length ? (
                    <Alert>
                        {constants.YOU_HAVE_YET_TO_ADD_A_CHART}
                        {constants.CLICK_ADD_CHART_TO_START}
                    </Alert>
                ) : null}
                <NewChartModal 
                    isVisible={isNewChartModalVisible}
                    setVisible={setIsNewChartModalVisible}
                    isNew={isNewChart}
                    existingChartData={chartToEditCloneOrRemove}
                    categoryOptions={categoryOptions}
                    saveChart={saveChart}
                />
                { removeConfirmationVisible ? (
                    <ConfirmationModal
                        friction='remove'
                        visible
                        header={`${constants.REMOVE_BUTTON_TEXT} ${chartToEditCloneOrRemove?.title} ${constants.CHART_LOWERCASE}?`}
                        onConfirm={onConfirmRemoveChart}
                        onDismiss={() => setRemoveConfirmationVisible(false)}
                    />
                ) : null }
                { removeAllChartsConfirmationVisible ? (
                    <ConfirmationModal
                        friction='remove'
                        visible
                        header={`${constants.REMOVE_ALL} ${constants.CHARTS_LOWERCASE}?`}
                        onConfirm={onConfirmRemoveAllCharts}
                        onDismiss={() => setRemoveAllChartsConfirmationVisible(false)}
                    >
                        {constants.REMOVE_ALL_CHARTS_MSG}
                    </ConfirmationModal>
                ) : null}
                { restoreDefaultsConfirmationVisible ? (
                    <ConfirmationModal
                        friction='restore'
                        visible
                        header={constants.RESTORE_DEFAULT_CHARTS}
                        onConfirm={restoreDefaultCharts}
                        onDismiss={() => setRestoreDefaultsConfirmationVisible(false)}
                    >
                        {constants.RESTORE_DEFAULTS_MSG}
                    </ConfirmationModal>
                ) : null }
            </SpaceBetween>
        );
    }
};

export default withFlashContext(DashboardTab);
